import { Injectable, signal } from '@angular/core';
import { jwtDecode } from 'jwt-decode';

export interface AuthClaims {
  scp: string;
  oid: string;
  sub: string;
  name: string;
  azp: string;
  preferred_username: string;
  roles: string[];
}

@Injectable({ providedIn: 'root' })
export class TokenManagerService {
  private accessToken = signal<string | null>(
    localStorage.getItem('bearer-token')
  );

  constructor() {
    // hydrate scopes from stored token on page load/refresh
    const token = this.accessToken();
    if (token) {
      this.accessTokenScopes = new Set(this.extractScopes(token));
    }
  }

  private accessTokenScopes: Set<string> = new Set<string>();

  private extractScopes(token: string): string[] {
    try {
      const claims = jwtDecode<AuthClaims>(token);
      return (claims.scp ?? '').split(' ').filter(Boolean);
    } catch {
      return [];
    }
  }

  getAuthClaims(): AuthClaims | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }
    try {
      return jwtDecode<AuthClaims>(token);
    } catch {
      return null;
    }
  }

  setAccessToken(token: string) {
    this.accessToken.set(token);
    localStorage.setItem('bearer-token', token);
    this.accessTokenScopes = new Set(this.extractScopes(token));
  }

  getAccessToken(): string | null {
    return this.accessToken();
  }

  clearAccessToken(): void {
    this.accessToken.set(null);
    localStorage.removeItem('bearer-token');
    this.accessTokenScopes.clear();
  }

  containsAllScopes(scopes: string[]): boolean {
    return scopes.every((scp) => this.accessTokenScopes.has(scp));
  }

  containsAnyScopes(scopes: string[]): boolean {
    return scopes.some((scp) => this.accessTokenScopes.has(scp));
  }

  getScopes(): Set<string> {
    return this.accessTokenScopes;
  }
}
